import type { CSSProperties } from 'react';

import type { DOMOutputSpec } from '@tiptap/pm/model';
import classnames from 'classnames';

import { textColorsFallback } from '@/assets';
import {
  computeColor,
  convertToHTMLAttributes,
  getBackgroundColorStyle,
  getTextAlignStyle,
  parseTRBL,
  TRBLString,
} from '@/utils';

import { SECTION_DEFAULTS } from './Section.constants';
import styles from './Section.css';
import type { SectionElementProps, TSectionElement } from './Section.type';

export function getSectionInnerWrapperAttributes(props: SectionElementProps) {
  const { element } = props;

  const { innerHorizontalAlign, innerVerticalAlign, textAlign, gap, innerWidth, innerWidthMax, isViewportWidth } =
    element.attrs || {};

  return {
    className: classnames('flex flex-col w-full grow', {
      [styles.alignLeft]: innerHorizontalAlign === 'left',
      [styles.alignCenter]: innerHorizontalAlign === 'center' || !innerHorizontalAlign,
      [styles.alignRight]: innerHorizontalAlign === 'right',
      [styles.alignTop]: innerVerticalAlign === 'align_start',
      [styles.alignMiddle]: innerVerticalAlign === 'align_center',
      [styles.alignBottom]: innerVerticalAlign === 'align_end',
    }),
    style: {
      maxWidth: isViewportWidth ? '100dvw' : innerWidthMax || SECTION_DEFAULTS.innerWidthMax,
      margin: 'auto',
      gap: gap,
      ...getTextAlignStyle(textAlign),
      '--width': innerWidth || SECTION_DEFAULTS.innerWidth,
    } as CSSProperties,
  };
}

function getSectionInnerWrapperRenderHTML({ node }: { node: Node }): [string, ...any[]] {
  return [
    'div',
    convertToHTMLAttributes(
      getSectionInnerWrapperAttributes({ element: node as unknown as TSectionElement, children: [] }),
    ),
    0,
  ];
}

export function getSectionRootAttributes(props: SectionElementProps) {
  const { children: _children, ...restProps } = props;
  const { element, attributes, className, backgroundImageElement: _backgroundImageElement, ...rest } = restProps;
  const {
    backgroundColor,
    customAttributes,
    padding,
    mobilePadding,
    horizontalAlign,
    color,
    textAlign,
    isViewportHeight,
    inAnimation,
    height = 'auto',
    hasUserSetMobilePadding,
    anchor,
  } = element.attrs || {};

  const { top, bottom } = parseTRBL(padding ?? SECTION_DEFAULTS.padding);

  const parsedMobilePadding = parseTRBL(mobilePadding ?? SECTION_DEFAULTS.paddingMobile);

  const mobilePaddingString = TRBLString({
    top,
    right: parsedMobilePadding.right,
    bottom,
    left: parsedMobilePadding.left,
  });

  const renderableMobilePadding = hasUserSetMobilePadding ? mobilePadding : mobilePaddingString;

  return {
    ...customAttributes,
    id: anchor || undefined,
    className: classnames(
      className,
      styles.sectionElement,
      {
        [styles.fadeInAnimation]: inAnimation === 'fade',
        [styles.slideInAnimation]: inAnimation === 'slide',
        [styles.growInAnimation]: inAnimation === 'grow',
        [styles.shrinkInAnimation]: inAnimation === 'shrink',
      },
      {
        [styles.alignLeft]: horizontalAlign === 'left',
        [styles.alignCenter]: horizontalAlign === 'center' || !horizontalAlign,
        [styles.alignRight]: horizontalAlign === 'right',
      },
      'bg-cover bg-no-repeat',
    ),
    style: {
      position: 'relative',
      color: computeColor(color, textColorsFallback),
      ...getBackgroundColorStyle(backgroundColor),
      ...getTextAlignStyle(textAlign),
      '--min-height': isViewportHeight ? '100dvh' : height,
      '--padding': padding ?? SECTION_DEFAULTS.padding,
      '--padding-root': padding ?? SECTION_DEFAULTS.paddingRoot,
      '--padding-mobile': renderableMobilePadding || SECTION_DEFAULTS.paddingMobile,
      '--padding-root-mobile': renderableMobilePadding || SECTION_DEFAULTS.paddingRootMobile,
    } as CSSProperties & { [key: `--${string}`]: string | undefined },
    ...attributes,
    ...rest,
  };
}

function getSectionRootRenderHTML({ node }: { node: Node }): [string, ...any[]] {
  return [
    'section',
    convertToHTMLAttributes(getSectionRootAttributes({ element: node as unknown as TSectionElement, children: [] })),
  ];
}

export function getSectionElementRenderHTML({ node }: { node: Node }): DOMOutputSpec {
  return [...getSectionRootRenderHTML({ node }), [...getSectionInnerWrapperRenderHTML({ node })]];
}
